import { Button } from 'primereact/button';
import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Avatar } from 'primereact/avatar';
import './profileStyles.css';

import { type IInfoCardProps, InfoCard } from './components/InfoCard';
import { ProgressBar } from 'primereact/progressbar';
import { TabPanel, TabView } from 'primereact/tabview';
import { Permissions, UserTrips } from './tabs';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import TitleValueIcon, {
  type ITitleValueIconProps,
} from './components/titleValueIcon/TitleValueIcon';
import { Icons } from '../../assets/icons';
import DateTimeUtils from '../../utils/DateTimeUtils';
import UserSearch from '../../components/UserSearch';
import Telematics from './tabs/Telematics';
import { useDemoMode } from '../../contexts/DemoModeContext';
import { maskEmail, maskFullName, maskDob } from '../../utils/piiMasking';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Checkbox } from 'primereact/checkbox';
import useTenantStore, { selectTenantId } from '../../stores/TenantStore';
import OpenAPI from '../../endpoint/Openapi';
import { type paths } from '../../endpoint/Schema';
import { noop } from '../../utils/GeneralUtils';
import { type Menu } from 'primereact/menu';

function Profile() {
  const params = useParams();
  const userId = params.userId ?? '';
  const navigate = useNavigate();
  const tenantId = useTenantStore(selectTenantId);
  const { isDemoModeActive } = useDemoMode();
  const DEFAULT_ACTIVE_TAB = 0;
  const activeTabIndex = params.tab ?? DEFAULT_ACTIVE_TAB;
  const menu = useRef<Menu>(null);
  const [activeIndex, setActiveIndex] = React.useState<number>(
    Number(activeTabIndex),
  );
  const [userData, setUserData] =
    React.useState<
      paths['/api/v2/admin/users/{userId}']['get']['responses']['200']['content']['application/json']
    >();
  const [telematicsData, setTelematicsData] =
    React.useState<
      paths['/api/v2/admin/users/{userId}/telematics']['get']['responses']['200']['content']['application/json']
    >();
  const [aggregateData, setAggregateData] = React.useState<
    | paths['/api/v2/admin/users/{userId}/statistic']['get']['responses']['200']['content']['application/json']
    | null
  >(null);
  const [tripsData, setTripsData] =
    React.useState<
      paths['/api/v2/admin/trips/{userId}']['get']['responses']['200']['content']['application/json']
    >();
  const [tripsSummaryData, setTripsSummaryData] =
    React.useState<
      paths['/api/v2/admin/trips/{userId}/summary']['get']['responses']['200']['content']['application/json']
    >();

  const getUser = React.useCallback(async () => {
    try {
      const res = await OpenAPI.GET('/api/v2/admin/users/{userId}', {
        params: {
          path: {
            userId,
          },
          query: {
            tenantId,
          },
        },
      });
      setUserData(res.data);
    } catch {}
  }, [tenantId, userId]);

  const getTelematics = React.useCallback(async () => {
    try {
      // Get recent data for "Last update" display
      const res = await OpenAPI.GET('/api/v2/admin/users/{userId}/telematics', {
        params: {
          path: {
            userId,
          },
          query: {
            tenantId,
            skip: 0,
            take: 1,
          },
        },
      });
      setTelematicsData(res.data);

      // Get all-time aggregate data from new backend endpoint
      const aggregateRes = await OpenAPI.GET(
        '/api/v2/admin/users/{userId}/statistic',
        {
          params: {
            path: {
              userId,
            },
            query: {
              tenantId,
              period: 'AllTime',
            },
          },
        },
      );
      setAggregateData(aggregateRes.data ?? null);
    } catch {}
  }, [tenantId, userId]);

  const getTrips = React.useCallback(async () => {
    try {
      const res = await OpenAPI.GET('/api/v2/admin/trips/{userId}', {
        params: {
          path: {
            userId,
          },
          query: {
            tenantId,
            skip: 0,
            take: 1,
          },
        },
      });
      setTripsData(res.data);
    } catch {}
  }, [tenantId, userId]);

  const getTripsSummary = React.useCallback(async () => {
    try {
      const res = await OpenAPI.GET('/api/v2/admin/trips/{userId}/summary', {
        params: {
          path: {
            userId,
          },
          query: {
            tenantId,
          },
        },
      });
      setTripsSummaryData(res.data);
    } catch {}
  }, [tenantId, userId]);

  useEffect(() => {
    getUser().catch(noop);
    getTelematics().catch(noop);
    getTrips().catch(noop);
    getTripsSummary().catch(noop);
  }, [getUser, getTelematics, getTrips, getTripsSummary]);

  const lifetime = DateTimeUtils.daysDiff(userData?.signUpDate);
  const [mileageIsComplete, setMileageIsComplete] = React.useState<boolean>(
    () => userData?.mileagePercent === 100,
  );
  const [loading, setLoading] = React.useState(false);
  const [userTypeLoading, setUserTypeLoading] = React.useState(false);

  async function changeMileage() {
    setLoading(true);

    try {
      await OpenAPI.POST('/api/v2/admin/users/{userId}/complete-mileage', {
        params: {
          path: {
            userId,
          },
          query: {
            tenantId,
          },
        },
      });
      getUser().catch(noop);
      setMileageIsComplete(userData?.mileagePercent === 100);
    } catch {}

    setLoading(false);
  }

  function onChangeMileage() {
    // show confirmation modal
    confirmDialog({
      message: 'Are you sure you want to proceed?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: changeMileage,
      reject: noop,
    });
  }

  async function changeUserType(
    newUserType: 'User' | 'Tester' | 'NotInWhiteList',
  ) {
    setUserTypeLoading(true);

    try {
      await OpenAPI.POST('/api/v2/admin/users/{userId}/user-type', {
        params: {
          path: {
            userId,
          },
          query: {
            tenantId,
          },
        },
        body: newUserType,
      });
      getUser().catch(noop);
    } catch {
      // Error handling can be added here if needed
    }

    setUserTypeLoading(false);
  }

  function onChangeUserType(newUserType: 'User' | 'Tester' | 'NotInWhiteList') {
    if (newUserType === userData?.userType) return;

    confirmDialog({
      message: `Are you sure you want to change the user type to ${newUserType}?`,
      header: 'Change User Type',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        await changeUserType(newUserType);
      },
      reject: noop,
    });
  }

  // render methods
  function renderDetailInfo() {
    const detailItems: IInfoCardProps[] = [
      {
        label: 'Sign up',
        icon: Icons.ic_signup,
        details: [
          {
            title: 'Sign up date: ',
            value: DateTimeUtils.formatDate(userData?.signUpDate),
          },
          {
            title: 'Lifetime:  ',
            value: lifetime === 1 ? `${lifetime} day` : `${lifetime} days`,
          },
          {
            title: 'First score:',
            value:
              userData?.completeMileagePercentDate != null
                ? DateTimeUtils.formatDate(userData?.completeMileagePercentDate)
                : '-',
          },
          {
            title: 'Score Readiness',
            value: `${userData?.mileagePercent ?? 0}%`,
          },
        ],
        extra: (
          <>
            <ProgressBar value={userData?.mileagePercent} />
            {userData?.manuallySetMileagePercentDate ? (
              <p className="mt-2 text-center font-bold">
                Score is completed manually
              </p>
            ) : (
              <div className="my-2 justify-center">
                {loading ? (
                  <ProgressSpinner
                    style={{
                      width: '15px',
                      height: '15px',
                      alignSelf: 'center',
                    }}
                  />
                ) : (
                  <Checkbox
                    inputId="mileage"
                    name="mileage"
                    value="CompleteMileage"
                    onChange={onChangeMileage}
                    checked={mileageIsComplete}
                    disabled={loading || mileageIsComplete}
                  />
                )}
                <label
                  className={`ml-2 text-primary font-bold`}
                  style={{ opacity: mileageIsComplete ? 0.4 : 1 }}
                >
                  Complete the percentage
                </label>
              </div>
            )}
          </>
        ),
      },
      {
        label: 'Telematics',
        icon: Icons.ic_car_side,
        details: [
          {
            title: 'Last update',
            value: DateTimeUtils.formatDate(telematicsData?.page?.[0]?.toDate),
          },
          {
            title: 'Safety Score',
            value: `${aggregateData?.safetyScore ? Math.round(aggregateData.safetyScore) : '-'}`,
          },
          {
            title: 'Acceleration Score',
            value: `${aggregateData?.accelerationScore ? Math.round(aggregateData.accelerationScore) : '-'}`,
          },
          {
            title: 'Braking Score',
            value: `${aggregateData?.brakingScore ? Math.round(aggregateData.brakingScore) : '-'}`,
          },
          {
            title: 'Cornering Score',
            value: `${aggregateData?.corneringScore ? Math.round(aggregateData.corneringScore) : '-'}`,
          },
          {
            title: 'Speeding Score',
            value: `${aggregateData?.speedingScore ? Math.round(aggregateData.speedingScore) : '-'}`,
          },
          {
            title: 'Phone Usage Score',
            value: `${aggregateData?.phoneUsageScore ? Math.round(aggregateData.phoneUsageScore) : '-'}`,
          },
        ],
      },
      {
        label: 'Trips',
        icon: Icons.ic_trips,
        details: [
          {
            title: 'Last trip',
            value: DateTimeUtils.formatDate(tripsData?.page?.[0]?.startDate),
          },
          {
            title: 'Trips',
            value: `${tripsSummaryData?.tripCounts ?? 0}`,
          },
          {
            title: 'Mileage',
            value: `${tripsSummaryData?.totalMileage ?? 0}`,
          },
        ],
      },
      {
        label: 'Rewards',
        icon: Icons.ic_acorn,
        details: [
          {
            title: 'Earned Acorn',
            value: `${userData?.totalEarnedAcorns ?? 0}`,
          },
          {
            title: 'Voucher',
            value: `£${Math.abs(userData?.cashbackAmount ?? 0)}`,
          },
        ],
      },
    ];
    return (
      <div className="detailBoxesContainer">
        {detailItems.map((item) => {
          return (
            <InfoCard
              key={'detail_' + item.label}
              label={item.label}
              details={item.details}
              icon={item.icon}
              extra={item.extra}
            />
          );
        })}
      </div>
    );
  }

  function renderHeader() {
    const displayEmail = isDemoModeActive
      ? maskEmail(userData?.email)
      : (userData?.email ?? '_');
    const displayDob = isDemoModeActive
      ? maskDob(userData?.birthDate)
      : DateTimeUtils.formatDate(userData?.birthDate);
    const displayName = isDemoModeActive
      ? maskFullName(userData?.givenName, userData?.surname)
      : `${userData?.givenName} ${userData?.surname}`;
    const avatarLabel = isDemoModeActive
      ? '**'
      : userData?.givenName != null && userData?.surname != null
        ? `${userData?.givenName.slice(0, 1)}${userData?.surname.slice(0, 1)}`
        : 'U';

    const items: ITitleValueIconProps[] = [
      {
        icon: Icons.ic_user,
        title: 'User ID',
        value: `${userData?.customerId}`,
      },

      {
        icon: Icons.ic_email,
        title: 'Email',
        value: displayEmail,
      },
      {
        icon: Icons.ic_birthdate,
        title: 'Date of birth',
        value: displayDob,
      },
    ];
    return (
      <div className="headerContainer">
        <div className="avatarContainer">
          <Avatar
            label={avatarLabel}
            icon="pi pi-user"
            className="profileAvatar"
            size="xlarge"
            shape="circle"
          />
        </div>
        <div className="card">
          <div className="nameContainer">
            <div>
              <Button
                type="button"
                iconPos="right"
                icon="pi pi-angle-down"
                label={displayName}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-color-secondary)',
                  borderWidth: 0,
                }}
                onClick={(event) => menu?.current?.toggle(event)}
              />
            </div>
          </div>
          <div className="topRowContainer">
            {items.map((item) => {
              return (
                <TitleValueIcon
                  key={'profile_' + item.title}
                  title={item.title}
                  value={item.value}
                  icon={item.icon}
                  containerClassName="titleContainer"
                />
              );
            })}
            {/* Custom User Type Section with Buttons */}
            <div className="tviContainer titleContainer">
              <div className="horizontalContainer">
                <img alt="User Type" src={Icons.ic_user} />
                <p className="tviTitle">User Type</p>
              </div>

              <div className="flex gap-2 mt-2 flex-wrap">
                <Button
                  label="User"
                  size="small"
                  severity={
                    userData?.userType === 'User' ? 'success' : 'secondary'
                  }
                  outlined={userData?.userType !== 'User'}
                  loading={userTypeLoading}
                  disabled={userTypeLoading || userData?.userType === 'User'}
                  onClick={() => {
                    onChangeUserType('User');
                  }}
                />
                <Button
                  label="Tester"
                  size="small"
                  severity={
                    userData?.userType === 'Tester' ? 'success' : 'secondary'
                  }
                  outlined={userData?.userType !== 'Tester'}
                  loading={userTypeLoading}
                  disabled={userTypeLoading || userData?.userType === 'Tester'}
                  onClick={() => {
                    onChangeUserType('Tester');
                  }}
                />
                <Button
                  label="NotInWhiteList"
                  size="small"
                  severity={
                    userData?.userType === 'NotInWhiteList'
                      ? 'danger'
                      : 'secondary'
                  }
                  outlined={userData?.userType !== 'NotInWhiteList'}
                  loading={userTypeLoading}
                  disabled={
                    userTypeLoading || userData?.userType === 'NotInWhiteList'
                  }
                  onClick={() => {
                    onChangeUserType('NotInWhiteList');
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderTabLayout() {
    const profileTabs = [
      { label: 'Telematics', content: <Telematics userId={userId} /> },
      { label: 'Trips', content: <UserTrips userId={userId} /> },
      { label: 'Permissions', content: <Permissions userId={userId} /> },
    ];
    return (
      <div className="tabLayoutContainer">
        <TabView
          activeIndex={activeIndex}
          onTabChange={(e) => {
            setActiveIndex(e.index);
          }}
        >
          {profileTabs.map((tab) => {
            return (
              <TabPanel key={tab.label} header={tab.label}>
                {tab.content}
              </TabPanel>
            );
          })}
        </TabView>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 p-2.5">
        <UserSearch
          tenantId={tenantId}
          handleNewUserSelected={(e) => {
            navigate('/user/' + e.id);
          }}
          handleInputOnChange={(e) => e}
        />
        {renderHeader()}
        {renderDetailInfo()}
        {renderTabLayout()}
      </div>
      <ConfirmDialog />
    </>
  );
}

export default Profile;
